import type { RenderDiff } from '../types';

/**
 * Browser-side mirror of api/lib/render-diff.ts — uses DOMParser instead
 * of node-html-parser so it adds zero dependencies to the bundle.
 *
 * Used when the server-side SSR fetch was blocked (Cloudflare etc.) and
 * the user pastes their own SSR view to compute the diff manually.
 */

interface Snapshot {
  schemaTypes: string[];
  headingsCount: number;
  h1Text: string | null;
  titleText: string | null;
  metaPresent: {
    viewport: boolean;
    canonical: boolean;
    description: boolean;
    openGraph: boolean;
    twitterCard: boolean;
  };
  size: number;
}

function snapshot(html: string): Snapshot {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const schemaTypes = new Set<string>();
  doc.querySelectorAll('script[type="application/ld+json"]').forEach((s) => {
    const raw = s.textContent;
    if (!raw) return;
    try {
      walkForTypes(JSON.parse(raw), schemaTypes);
    } catch {
      /* ignore malformed JSON-LD */
    }
  });

  const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
  const h1 = doc.querySelector('h1');
  const titleEl = doc.querySelector('title');

  let viewport = false;
  let description = false;
  let openGraph = false;
  let twitterCard = false;
  doc.querySelectorAll('meta').forEach((m) => {
    const name = (m.getAttribute('name') || '').toLowerCase();
    const property = (m.getAttribute('property') || '').toLowerCase();
    const content = (m.getAttribute('content') || '').trim();
    if (name === 'viewport' && content) viewport = true;
    if (name === 'description' && content) description = true;
    if (property.startsWith('og:')) openGraph = true;
    if (name.startsWith('twitter:')) twitterCard = true;
  });

  let canonical = false;
  doc.querySelectorAll('link').forEach((l) => {
    if ((l.getAttribute('rel') || '').toLowerCase() === 'canonical' &&
        (l.getAttribute('href') || '').trim()) {
      canonical = true;
    }
  });

  return {
    schemaTypes: Array.from(schemaTypes).sort(),
    headingsCount: headings.length,
    h1Text: h1?.textContent?.trim() || null,
    titleText: titleEl?.textContent?.trim() || null,
    metaPresent: { viewport, canonical, description, openGraph, twitterCard },
    size: html.length,
  };
}

function walkForTypes(node: unknown, out: Set<string>): void {
  if (!node) return;
  if (Array.isArray(node)) {
    for (const item of node) walkForTypes(item, out);
    return;
  }
  if (typeof node !== 'object') return;
  const obj = node as Record<string, unknown>;
  const t = obj['@type'];
  if (typeof t === 'string') out.add(t);
  else if (Array.isArray(t)) for (const v of t) if (typeof v === 'string') out.add(v);
  for (const value of Object.values(obj)) {
    if (value && typeof value === 'object') walkForTypes(value, out);
  }
}

export function computeDiffClient(ssrHtml: string, csrHtml: string): RenderDiff {
  const ssr = snapshot(ssrHtml);
  const csr = snapshot(csrHtml);
  const ssrTypes = new Set(ssr.schemaTypes);
  const csrTypes = new Set(csr.schemaTypes);
  return {
    ssrAvailable: true,
    ssrSize: ssr.size,
    csrSize: csr.size,
    schemaTypesAddedByJs: Array.from(csrTypes).filter((t) => !ssrTypes.has(t)).sort(),
    schemaTypesRemovedByJs: Array.from(ssrTypes).filter((t) => !csrTypes.has(t)).sort(),
    h1AddedByJs: !ssr.h1Text && !!csr.h1Text,
    titleAddedByJs: !ssr.titleText && !!csr.titleText,
    headingsCountSsr: ssr.headingsCount,
    headingsCountCsr: csr.headingsCount,
    metaAddedByJs: {
      viewport: !ssr.metaPresent.viewport && csr.metaPresent.viewport,
      canonical: !ssr.metaPresent.canonical && csr.metaPresent.canonical,
      description: !ssr.metaPresent.description && csr.metaPresent.description,
      openGraph: !ssr.metaPresent.openGraph && csr.metaPresent.openGraph,
      twitterCard: !ssr.metaPresent.twitterCard && csr.metaPresent.twitterCard,
    },
  };
}
